/**
 * liveDocLoadTracker — pure state helpers for the spine's per-section
 * "loading into Word…" tracker.
 *
 * After a redraft the section's new text is committed to the server SSOT and
 * the add-in re-projects just that section into the live Word document
 * (``reinsertModuleIntoLiveDoc`` → ``insertSectionContent``). That write is
 * async, can be deferred while a Load is still settling, and can silently
 * no-op when the section's heading isn't present in the open doc. This tracker
 * makes the write observable on the spine row: ``loading`` while the write is
 * in flight, cleared on a confirmed write (``sectionsWritten > 0``), and
 * ``failed`` when nothing was written (heading not found / doc not Loaded) or
 * the write threw.
 *
 * The state is a plain ``Map`` owned by the shell and passed into ``LoomSpine``
 * as a prop — deliberately NOT stored on ``SpineSection``, so it is immune to
 * the SSE ``section_state_changed`` subline-wipe and to spine rebuilds. These
 * helpers are pure (return a new Map; never mutate the input) so the
 * transitions are unit-testable without rendering the shell.
 */

export type LiveDocLoadState = "loading" | "failed";

export interface LiveDocLoadEntry {
  state: LiveDocLoadState;
  /** Wall-clock ms when the CURRENT loading attempt started; drives the spine
   *  elapsed timer, and is preserved into the ``failed`` state so the row can
   *  still show how long it tried. */
  startedAt: number;
}

export type LiveDocLoadMap = ReadonlyMap<string, LiveDocLoadEntry>;

/** Mark ``sectionId`` as loading into Word, stamping the attempt start. */
export function setLiveDocLoading(map: LiveDocLoadMap, sectionId: string, now: number): LiveDocLoadMap {
  const next = new Map(map);
  next.set(sectionId, { state: "loading", startedAt: now });
  return next;
}

/** Clear ``sectionId`` (a confirmed successful write, or a fresh regen/Load). */
export function clearLiveDocLoad(map: LiveDocLoadMap, sectionId: string): LiveDocLoadMap {
  if (!map.has(sectionId)) return map;
  const next = new Map(map);
  next.delete(sectionId);
  return next;
}

/** Mark ``sectionId`` as failed, preserving the original ``startedAt`` when a
 *  loading entry exists so the elapsed reading stays stable across the flip. */
export function setLiveDocFailed(map: LiveDocLoadMap, sectionId: string, now: number): LiveDocLoadMap {
  const prev = map.get(sectionId);
  const next = new Map(map);
  next.set(sectionId, { state: "failed", startedAt: prev?.startedAt ?? now });
  return next;
}

/** True when any section is currently loading — used to keep the spine's
 *  elapsed-timer tick running even when no run is actively streaming. */
export function hasActiveLiveDocLoad(map: LiveDocLoadMap): boolean {
  for (const entry of map.values()) {
    if (entry.state === "loading") return true;
  }
  return false;
}

/** Classify a ``reinsertModuleIntoLiveDoc`` result into the tracker outcome.
 *  ``sectionsWritten > 0`` is the only success; 0 (no-op) is a failure. */
export function outcomeFromSectionsWritten(sectionsWritten: number): "success" | "failed" {
  return sectionsWritten > 0 ? "success" : "failed";
}
